import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeftRight,
  Check,
  ChevronDown,
  ChevronUp,
  GripVertical,
  Plus,
  Trash2,
} from 'lucide-react';
import { QuestionnaireFormPicker } from '@/components/patientReports/QuestionnaireFormPicker';
import { StatusChip } from '@/components/ui/Chip';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { MenuItem, MenuPopover } from '@/components/ui/Popover';
import { Radio } from '@/components/ui/Radio';
import { Select } from '@/components/ui/Select';
import { GrowingTextarea } from '@/components/patientReports/GrowingTextarea';
import {
  EMPTY_REPORT_CATALOG,
  type FileSource,
  type JournalNoteSource,
  type MarkingSource,
  type QuestionnaireSource,
} from '@/lib/patientReports/model';
import { orderQuestionnaireFormOptions } from '@/lib/patientReports/questionnaireFormOptions';
import {
  questionnaireChartChecked,
  setQuestionnaireCharts,
  toggleQuestionnaireChart,
} from '@/lib/patientReports/questionnaireCharts';
import {
  PATIENT_FIELD_KEYS,
  createReportSection,
  headingStyleOf,
  moveField,
  removeSection,
  reorderSections,
  selectedJournalNotes,
  toggleField,
  updateSection,
  withSectionKind,
  type HeadingStyle,
  type PatientFieldKey,
  type ReportSection,
  type ReportSectionKind,
  type TocNumbering,
} from '@/lib/patientReports/template';
import { personalTextChoiceLabel } from '@/lib/patientReports/userTexts';
import { FORM_LABEL_TYPE, TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

const TOC_NUMBERING_ORDER: readonly TocNumbering[] = ['numbers', 'letters', 'none'];

const HEADING_STYLE_ORDER: readonly HeadingStyle[] = ['bold', 'italic', 'both'];

const HEADING_STYLE_LABEL: Record<HeadingStyle, string> = {
  bold: 'patientReports.heading_bold',
  italic: 'patientReports.heading_italic',
  both: 'patientReports.heading_both',
};

const TOC_NUMBERING_LABEL: Record<TocNumbering, string> = {
  numbers: 'patientReports.toc_numbers',
  letters: 'patientReports.toc_letters',
  none: 'patientReports.toc_none',
};

const SECTION_KIND_OPTIONS = [
  'text',
  'user_text',
  'patient_fields',
  'journal',
  'markings',
  'questionnaire',
  'file',
  'toc',
] as const satisfies readonly ReportSectionKind[];

type SectionKindOption = (typeof SECTION_KIND_OPTIONS)[number];

const SECTION_KIND_LABEL: Record<SectionKindOption, string> = {
  text: 'patientReports.text_heading',
  user_text: 'patientReports.add_user_text',
  patient_fields: 'patientReports.add_patient',
  journal: 'patientReports.add_journal',
  markings: 'patientReports.add_markings',
  questionnaire: 'patientReports.add_questionnaire',
  file: 'patientReports.kind_file',
  toc: 'patientReports.add_toc',
};

function isSectionKindOption(value: string): value is SectionKindOption {
  return (SECTION_KIND_OPTIONS as readonly string[]).includes(value);
}

const FIELD_LABEL: Record<PatientFieldKey, string> = {
  name: 'patientReports.field_name',
  cpr: 'patientReports.field_cpr',
  contact: 'patientReports.field_contact',
  address: 'patientReports.field_address',
  job_title: 'patientReports.field_job',
  cave: 'patientReports.field_cave',
  notes: 'patientReports.field_notes',
  diagnosis: 'patientReports.field_diagnosis',
  pat_number: 'patientReports.field_pat_number',
  locale: 'patientReports.field_locale',
};

export function ReportSectionCards({
  sections,
  onChange,
  tags,
  questions,
  charts,
  formOptions,
  patientQuestionnaires,
  journalNotes,
  markings,
  files,
  missingFormIds,
  textTypes,
  userTexts,
  disabled,
}: {
  sections: ReportSection[];
  onChange: (sections: ReportSection[]) => void;
  tags: readonly { id: string; name: string }[];
  questions: Readonly<Record<string, readonly { id: string; text: string }[]>>;
  /** Figures this questionnaire can draw. Each row is its own switch. */
  charts?: Readonly<Record<string, readonly { id: string; label: string }[]>>;
  formOptions?: readonly { id: string; name: string }[];
  /** This patient's questionnaire copies. Filled ones are marked in the picker. */
  patientQuestionnaires?: readonly QuestionnaireSource[];
  /** This patient's notes. Omitted on a template, where notes are chosen later. */
  journalNotes?: readonly JournalNoteSource[];
  /** This patient's markings. Omitted on a template, where markings are chosen later. */
  markings?: readonly MarkingSource[];
  /** This patient's files. Omitted on a template, where files are chosen later. */
  files?: readonly FileSource[];
  /** Questionnaire template ids named on the report but not found on the patient. */
  missingFormIds?: readonly string[];
  /** Clinic text types. The section stores the id, not a behandler's wording. */
  textTypes?: readonly { id: string; name: string }[];
  /** This behandler's saved texts. Omitted on a clinic template, which only names the tag. */
  userTexts?: readonly { id: string; typeId: string; body: string }[];
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [awaitingType, setAwaitingType] = useState<ReadonlySet<string>>(() => new Set());
  const focusKindId = useRef<string | null>(null);
  const focusHeadingId = useRef<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const insertAfter = (afterId: string | null) => {
    const section = createReportSection('text', '');
    const found =
      afterId == null ? sections.length - 1 : sections.findIndex((item) => item.id === afterId);
    const at = found < 0 ? sections.length : found + 1;
    const next = [...sections];
    next.splice(at, 0, section);
    onChange(next);
    setAwaitingType((current) => new Set(current).add(section.id));
    setExpandedId(section.id);
    focusKindId.current = section.id;
  };
  useEffect(() => {
    const id = focusKindId.current;
    if (!id) return;
    focusKindId.current = null;
    document.getElementById(`report-section-kind-${id}`)?.focus();
  }, [sections, expandedId]);
  useEffect(() => {
    const id = focusHeadingId.current;
    if (!id) return;
    focusHeadingId.current = null;
    document.getElementById(`report-section-heading-${id}`)?.focus();
  }, [sections, expandedId]);
  const commitKind = (section: ReportSection, kind: SectionKindOption) => {
    const wasAwaiting = awaitingType.has(section.id);
    const heading = headingFollowsKind(section, textTypes ?? [], t)
      ? t(SECTION_KIND_LABEL[kind])
      : section.heading;
    onChange(updateSection(sections, section.id, withSectionKind(section, kind, heading)));
    if (wasAwaiting) focusHeadingId.current = section.id;
    setAwaitingType((current) => {
      if (!current.has(section.id)) return current;
      const next = new Set(current);
      next.delete(section.id);
      return next;
    });
  };

  return (
    <div>
      {sections.length === 0 ? (
        <div className="flex items-center gap-s2">
          <SectionIconButton
            label={t('patientReports.add_section')}
            testId="report-add-section"
            disabled={disabled}
            onClick={() => insertAfter(null)}
          >
            <Plus className="h-4 w-4" strokeWidth={1.75} />
          </SectionIconButton>
          <p className={cn(TYPE.small, 'text-ds-muted')}>{t('patientReports.document_empty')}</p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(event: DragEndEvent) => {
            const overId = event.over ? String(event.over.id) : null;
            if (!overId) return;
            onChange(reorderSections(sections, String(event.active.id), overId));
          }}
        >
          <SortableContext
            items={sections.map((section) => section.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex flex-col gap-s3">
              {sections.map((section) => (
                <SectionCard
                  key={section.id}
                  section={section}
                  tags={tags}
                  questions={questions[section.id] ?? []}
                  charts={charts?.[section.id] ?? []}
                  formOptions={formOptions}
                  patientQuestionnaires={patientQuestionnaires}
                  journalNotes={journalNotes}
                  markings={markings}
                  files={files}
                  textTypes={textTypes ?? []}
                  userTexts={userTexts}
                  missing={questionnaireCardIsMissing(section, missingFormIds)}
                  awaitingType={awaitingType.has(section.id)}
                  expanded={expandedId === section.id}
                  disabled={disabled}
                  onToggle={() =>
                    setExpandedId((current) => (current === section.id ? null : section.id))
                  }
                  onChange={(next) => onChange(updateSection(sections, section.id, next))}
                  onKind={(kind) => commitKind(section, kind)}
                  onInsertBelow={() => insertAfter(section.id)}
                  onRemove={() => onChange(removeSection(sections, section.id))}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

function headingFollowsKind(
  section: ReportSection,
  textTypes: readonly { id: string; name: string }[],
  t: (key: string) => string,
): boolean {
  const current = section.heading.trim();
  if (!current) return true;
  if (section.kind === 'user_text') {
    const name = textTypes.find((type) => type.id === section.typeId)?.name.trim();
    if (name && current === name) return true;
  }
  if (section.kind === 'questionnaire' && current === section.formsTemplateName.trim()) return true;
  if (!isSectionKindOption(section.kind)) return false;
  return current === t(SECTION_KIND_LABEL[section.kind]);
}

function sectionSummary(
  section: ReportSection,
  t: (key: string, options?: Record<string, string>) => string,
  missing: boolean,
  textTypes: readonly { id: string; name: string }[],
  awaitingType: boolean,
): string {
  let line: string;
  if (awaitingType) {
    line = t('patientReports.section_type_choose');
  } else if (section.kind === 'questionnaire') {
    const name = section.formsTemplateName.trim() || t('patientReports.form_none');
    line = missing ? `${name} · ${t('patientReports.form_missing')}` : name;
  } else if (section.kind === 'patient_fields') {
    const names = section.fields.map((field) => t(FIELD_LABEL[field]));
    line = names.join(', ') || t('patientReports.kind_patient_card');
  } else if (section.kind === 'journal') line = journalSelectionSummary(section, t);
  else if (section.kind === 'markings') line = markingSelectionSummary(section, t);
  else if (section.kind === 'file') line = fileSelectionSummary(section, t);
  else if (section.kind === 'toc') {
    // «Foran emnet» (Numre / Bogstaver / Intet) is how list entries are marked.
    // It is not a subtitle for the section card.
    const typeName = t(SECTION_KIND_LABEL.toc);
    line = section.heading.trim() === typeName ? '' : typeName;
  } else if (section.kind === 'user_text') {
    const typeName = textTypes.find((type) => type.id === section.typeId)?.name;
    line = typeName
      ? t('patientReports.user_text_summary', { name: typeName })
      : section.typeId
        ? t('patientReports.user_text_type_missing')
        : t('patientReports.user_text_type_none');
  } else line = section.body.trim().replace(/\s+/g, ' ') || t('patientReports.body');
  const extras: string[] = [];
  if (section.headingStyle === 'italic') extras.push(t('patientReports.heading_italic'));
  if (section.headingStyle === 'both') extras.push(t('patientReports.heading_both'));
  if (section.pageBreakAfter === true) extras.push(t('patientReports.page_break_after'));
  return [line, ...extras].filter((part) => part.trim()).join(' · ');
}

function SectionIconButton({
  label,
  testId,
  disabled,
  quiet,
  onClick,
  children,
}: {
  label: string;
  testId?: string;
  disabled?: boolean;
  /** Trash sits beside +, and should not draw the eye first. */
  quiet?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'inline-flex h-7 w-7 items-center justify-center rounded-ctl text-ds-muted hover:bg-ds-neutral-tint hover:text-ds-text disabled:cursor-not-allowed disabled:opacity-60',
        quiet && 'opacity-60 hover:opacity-100',
      )}
    >
      {children}
    </button>
  );
}

function SectionKindControl({
  section,
  awaitingType,
  disabled,
  onKind,
}: {
  section: ReportSection;
  awaitingType: boolean;
  disabled?: boolean;
  onKind: (kind: SectionKindOption) => void;
}) {
  const { t } = useTranslation();
  const choose = (value: string) => {
    if (!isSectionKindOption(value)) return;
    // A new section is stored as text until a type is picked. Choosing Tekst must still commit.
    if (!awaitingType && value === section.kind) return;
    onKind(value);
  };
  if (awaitingType) {
    return (
      <Select
        id={`report-section-kind-${section.id}`}
        data-testid={`report-section-kind-${section.id}`}
        label={t('patientReports.section_type')}
        hint={t('patientReports.section_type_hint')}
        disabled={disabled}
        value=""
        onChange={(event) => choose(event.target.value)}
      >
        <option value="">{t('patientReports.section_type_choose')}</option>
        {SECTION_KIND_OPTIONS.map((kind) => (
          <option key={kind} value={kind}>
            {t(SECTION_KIND_LABEL[kind])}
          </option>
        ))}
      </Select>
    );
  }
  const typeName = isSectionKindOption(section.kind) ? t(SECTION_KIND_LABEL[section.kind]) : '';
  return (
    <MenuPopover
      align="end"
      title={t('patientReports.section_type_change')}
      trigger={
        <button
          type="button"
          data-testid={`report-section-kind-change-${section.id}`}
          disabled={disabled}
          title={t('patientReports.section_type_change_current', { type: typeName })}
          aria-label={t('patientReports.section_type_change_current', { type: typeName })}
          className="inline-flex h-8 shrink-0 items-center gap-1 whitespace-nowrap rounded-ctl px-s2 text-ds-muted hover:bg-ds-neutral-tint hover:text-ds-text disabled:cursor-not-allowed disabled:opacity-60"
        >
          <ArrowLeftRight className="h-3.5 w-3.5" aria-hidden />
          <span className={TYPE.small}>{t('patientReports.section_type_change')}</span>
        </button>
      }
    >
      {SECTION_KIND_OPTIONS.map((kind) => (
        <MenuItem
          key={kind}
          active={kind === section.kind}
          icon={
            kind === section.kind ? (
              <Check className="h-4 w-4" aria-hidden />
            ) : (
              <span className="inline-block h-4 w-4" aria-hidden />
            )
          }
          onClick={() => choose(kind)}
        >
          {t(SECTION_KIND_LABEL[kind])}
        </MenuItem>
      ))}
    </MenuPopover>
  );
}

function SectionCard({
  section,
  tags,
  questions,
  charts,
  formOptions,
  patientQuestionnaires,
  journalNotes,
  markings,
  files,
  textTypes,
  userTexts,
  missing,
  awaitingType,
  expanded,
  disabled,
  onToggle,
  onChange,
  onKind,
  onInsertBelow,
  onRemove,
}: {
  section: ReportSection;
  tags: readonly { id: string; name: string }[];
  questions: readonly { id: string; text: string }[];
  charts: readonly { id: string; label: string }[];
  formOptions?: readonly { id: string; name: string }[];
  patientQuestionnaires?: readonly QuestionnaireSource[];
  journalNotes?: readonly JournalNoteSource[];
  markings?: readonly MarkingSource[];
  files?: readonly FileSource[];
  textTypes: readonly { id: string; name: string }[];
  userTexts?: readonly { id: string; typeId: string; body: string }[];
  missing?: boolean;
  awaitingType: boolean;
  expanded: boolean;
  disabled?: boolean;
  onToggle: () => void;
  onChange: (section: ReportSection) => void;
  onKind: (kind: SectionKindOption) => void;
  onInsertBelow: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const sortable = useSortable({ id: section.id, disabled });
  const summary = sectionSummary(section, t, missing === true, textTypes, awaitingType);
  const questionnaireChoices =
    section.kind === 'questionnaire'
      ? orderQuestionnaireFormOptions(formOptions, patientQuestionnaires, {
          id: section.formsTemplateId,
          name: section.formsTemplateName,
        })
      : [];
  const questionnaireFilled =
    section.kind === 'questionnaire' &&
    missing !== true &&
    questionnaireChoices.some((choice) => choice.id === section.formsTemplateId && choice.filled);
  const headingId = `report-section-h-${section.id}`;
  const panelId = `report-section-b-${section.id}`;
  const headingFieldId = `report-section-heading-${section.id}`;
  const heading =
    section.heading.trim() ||
    (awaitingType ? t('patientReports.section_new') : t('patientReports.untitled'));
  return (
    <article
      ref={sortable.setNodeRef}
      style={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
      }}
      className={cn(
        'overflow-hidden rounded-ctl border border-ds-line bg-ds-bg',
        sortable.isDragging && 'opacity-70',
      )}
    >
      <div className="flex items-center gap-s1 px-s2 py-s2">
        <div className="flex min-w-0 flex-1 items-center gap-s1">
          <button
            type="button"
            className="cursor-grab rounded-ctl p-1 text-ds-muted hover:bg-ds-neutral-tint"
            aria-label={t('patientReports.drag')}
            {...sortable.attributes}
            {...sortable.listeners}
          >
            <GripVertical className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            id={headingId}
            className="flex min-w-0 flex-1 items-center gap-s2 rounded-ctl px-s1 py-s1 text-left hover:bg-ds-row-hover"
            aria-expanded={expanded}
            aria-controls={panelId}
            onClick={onToggle}
          >
            <span className="min-w-0 flex-1">
              <span className={cn(TYPE.body, 'block truncate font-medium text-ds-text')}>
                {heading}
              </span>
              {summary || questionnaireFilled ? (
                <span className="mt-0.5 flex min-w-0 items-center gap-s2">
                  {summary ? (
                    <span
                      data-testid={`report-section-summary-${section.id}`}
                      className={cn(
                        TYPE.small,
                        'min-w-0 truncate',
                        missing ? 'text-ds-red-ink' : 'text-ds-muted',
                      )}
                    >
                      {summary}
                    </span>
                  ) : null}
                  {questionnaireFilled ? (
                    <StatusChip tone="completed" className="shrink-0">
                      {t('patientReports.form_filled')}
                    </StatusChip>
                  ) : null}
                </span>
              ) : null}
            </span>
            <ChevronDown
              className={cn(
                'h-4 w-4 shrink-0 text-ds-muted transition-transform',
                expanded && 'rotate-180',
              )}
              strokeWidth={1.75}
              aria-hidden
            />
            <span className="sr-only">
              {expanded ? t('patientReports.section_collapse') : t('patientReports.section_expand')}
            </span>
          </button>
        </div>
        <div className="flex shrink-0 items-center">
          <SectionIconButton
            label={t('patientReports.add_section_below')}
            testId={`report-add-below-${section.id}`}
            disabled={disabled}
            onClick={onInsertBelow}
          >
            <Plus className="h-4 w-4" strokeWidth={1.75} />
          </SectionIconButton>
          <SectionIconButton
            label={t('patientReports.remove')}
            disabled={disabled}
            onClick={onRemove}
            quiet
          >
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
          </SectionIconButton>
        </div>
      </div>
      {expanded ? (
        <div
          id={panelId}
          role="region"
          aria-labelledby={headingId}
          className="border-t border-ds-line px-s3 py-s3"
        >
          {awaitingType ? (
            <div className="mb-s3 max-w-sm">
              <SectionKindControl
                section={section}
                awaitingType
                disabled={disabled}
                onKind={onKind}
              />
            </div>
          ) : null}
          <div>
            <div className="flex items-center justify-between gap-s2">
              <label htmlFor={headingFieldId} className={cn('min-w-0', FORM_LABEL_TYPE)}>
                {t('patientReports.heading')}
              </label>
              {awaitingType ? null : (
                <SectionKindControl
                  section={section}
                  awaitingType={false}
                  disabled={disabled}
                  onKind={onKind}
                />
              )}
            </div>
            <Input
              id={headingFieldId}
              className="mt-s1"
              value={section.heading}
              disabled={disabled}
              onChange={(event) => onChange({ ...section, heading: event.target.value })}
            />
          </div>
          {!awaitingType && section.kind === 'patient_fields' ? (
            <div className="mt-s3">
              <p className={cn(TYPE.small, 'text-ds-muted')}>{t('patientReports.fields_hint')}</p>
              <div className="mt-s2 flex flex-col gap-s1">
                {PATIENT_FIELD_KEYS.map((field) => {
                  const selected = section.fields.includes(field);
                  const fieldIndex = section.fields.indexOf(field);
                  return (
                    <div key={field} className="flex items-center gap-s2">
                      <Checkbox
                        label={t(FIELD_LABEL[field])}
                        checked={selected}
                        disabled={disabled}
                        onChange={(event) =>
                          onChange({
                            ...section,
                            fields: toggleField(section.fields, field, event.target.checked),
                          })
                        }
                      />
                      {selected ? (
                        <span className="ml-auto flex gap-s1">
                          <button
                            type="button"
                            aria-label={t('patientReports.move_up')}
                            disabled={disabled || fieldIndex === 0}
                            onClick={() =>
                              onChange({ ...section, fields: moveField(section.fields, field, -1) })
                            }
                          >
                            <ChevronUp className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            aria-label={t('patientReports.move_down')}
                            disabled={disabled || fieldIndex === section.fields.length - 1}
                            onClick={() =>
                              onChange({ ...section, fields: moveField(section.fields, field, 1) })
                            }
                          >
                            <ChevronDown className="h-4 w-4" />
                          </button>
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
          {section.kind === 'journal' ? (
            <JournalNotePicker
              section={section}
              notes={journalNotes}
              disabled={disabled}
              onChange={onChange}
            />
          ) : null}
          {section.kind === 'markings' ? (
            <div className="mt-s3 flex flex-col gap-s3">
              <Select
                label={t('patientReports.order_label')}
                disabled={disabled}
                value={section.order}
                onChange={(event) =>
                  onChange({
                    ...section,
                    order:
                      event.target.value === 'oldest' || event.target.value === 'file'
                        ? event.target.value
                        : 'newest',
                  })
                }
              >
                <option value="newest">{t('patientReports.order_newest')}</option>
                <option value="oldest">{t('patientReports.order_oldest')}</option>
                <option value="file">{t('patientReports.order_file')}</option>
              </Select>
              <MarkingPicker
                section={section}
                markings={markings}
                tags={tags}
                disabled={disabled}
                onChange={onChange}
              />
              <Checkbox
                label={t('patientReports.include_quote')}
                checked={section.includeQuote}
                disabled={disabled}
                onChange={(event) => onChange({ ...section, includeQuote: event.target.checked })}
              />
              <Checkbox
                label={t('patientReports.include_comment')}
                checked={section.includeComment}
                disabled={disabled}
                onChange={(event) => onChange({ ...section, includeComment: event.target.checked })}
              />
              <Checkbox
                label={t('patientReports.include_extract')}
                checked={section.includeFileExtract}
                disabled={disabled}
                onChange={(event) =>
                  onChange({ ...section, includeFileExtract: event.target.checked })
                }
              />
            </div>
          ) : null}
          {section.kind === 'questionnaire' ? (
            <div className="mt-s3 flex flex-col gap-s2">
              <QuestionnaireFormPicker
                sectionId={section.id}
                value={section.formsTemplateId ?? ''}
                choices={questionnaireChoices}
                missing={missing === true}
                disabled={disabled}
                onChange={(id) => {
                  const form = questionnaireChoices.find((item) => item.id === id);
                  const nextName = form?.name ?? (id ? section.formsTemplateName : '');
                  const followsForm =
                    !section.heading.trim() ||
                    section.heading === section.formsTemplateName ||
                    section.heading === t('patientReports.add_questionnaire');
                  onChange({
                    ...section,
                    heading: followsForm
                      ? nextName || t('patientReports.add_questionnaire')
                      : section.heading,
                    formsTemplateId: id || null,
                    formsTemplateName: nextName,
                    assignmentId: null,
                    chartIds: null,
                    allQuestions: true,
                    questionIds: [],
                  });
                }}
              />
              <Checkbox
                label={t('patientReports.part_answers')}
                checked={section.parts.answers}
                disabled={disabled}
                onChange={(event) =>
                  onChange({
                    ...section,
                    parts: { ...section.parts, answers: event.target.checked },
                  })
                }
              />
              <Checkbox
                label={t('patientReports.part_score')}
                checked={section.parts.score}
                disabled={disabled}
                onChange={(event) =>
                  onChange({ ...section, parts: { ...section.parts, score: event.target.checked } })
                }
              />
              <Checkbox
                label={t('patientReports.part_charts')}
                checked={section.parts.charts}
                disabled={disabled}
                onChange={(event) => {
                  const next = setQuestionnaireCharts(event.target.checked);
                  onChange({
                    ...section,
                    parts: { ...section.parts, charts: next.parts.charts },
                    chartIds: next.chartIds,
                  });
                }}
              />
              {charts.length > 0 ? (
                <div className="ml-7 flex flex-col gap-s1">
                  <p className={cn(TYPE.small, 'text-ds-muted')}>{t('patientReports.charts_each')}</p>
                  {charts.map((chart) => (
                    <Checkbox
                      key={chart.id}
                      label={<span className="line-clamp-2">{chart.label}</span>}
                      checked={questionnaireChartChecked(section, chart.id)}
                      disabled={disabled}
                      onChange={(event) => {
                        const next = toggleQuestionnaireChart(
                          section,
                          chart.id,
                          event.target.checked,
                          charts.map((item) => item.id),
                        );
                        onChange({
                          ...section,
                          parts: { ...section.parts, charts: next.charts },
                          chartIds: next.chartIds,
                        });
                      }}
                    />
                  ))}
                </div>
              ) : null}
              <Checkbox
                label={t('patientReports.part_comments')}
                checked={section.parts.comments}
                disabled={disabled}
                onChange={(event) =>
                  onChange({
                    ...section,
                    parts: { ...section.parts, comments: event.target.checked },
                  })
                }
              />
              <Checkbox
                label={t('patientReports.questions_all')}
                checked={section.allQuestions}
                disabled={disabled}
                onChange={(event) =>
                  onChange({
                    ...section,
                    allQuestions: event.target.checked,
                    questionIds: event.target.checked ? [] : section.questionIds,
                  })
                }
              />
              {!section.allQuestions && questions.length > 0 ? (
                <div>
                  <p className={cn(TYPE.small, 'font-semibold text-ds-text')}>
                    {t('patientReports.questions_zoom')}
                  </p>
                  <div className="mt-s2 flex flex-col gap-s1">
                    {questions.map((question) => (
                      <Checkbox
                        key={question.id}
                        label={<span className="line-clamp-2">{question.text}</span>}
                        checked={section.questionIds.includes(question.id)}
                        disabled={disabled}
                        onChange={(event) => {
                          const questionIds = event.target.checked
                            ? [...section.questionIds, question.id]
                            : section.questionIds.filter((id) => id !== question.id);
                          onChange({ ...section, questionIds });
                        }}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
          {!awaitingType && section.kind === 'text' ? (
            <GrowingTextarea
              id={`report-section-body-${section.id}`}
              className="mt-s2"
              label={t('patientReports.body')}
              value={section.body}
              disabled={disabled}
              data-testid={`report-section-body-${section.id}`}
              onChange={(event) => onChange({ ...section, body: event.target.value })}
            />
          ) : null}
          {!awaitingType && section.kind === 'file' ? (
            <FilePicker section={section} files={files} disabled={disabled} onChange={onChange} />
          ) : null}
          {!awaitingType && section.kind === 'user_text' ? (
            <UserTextFields
              section={section}
              textTypes={textTypes}
              userTexts={userTexts}
              disabled={disabled}
              onChange={onChange}
            />
          ) : null}
          {section.kind === 'toc' ? (
            <fieldset className="mt-s3" disabled={disabled}>
              <legend className={cn(TYPE.small, 'font-semibold text-ds-text')}>
                {t('patientReports.toc_numbering')}
              </legend>
              <p className={cn(TYPE.small, 'text-ds-muted')}>
                {t('patientReports.toc_numbering_hint')}
              </p>
              <div
                className="mt-s2 flex flex-col gap-s1"
                role="radiogroup"
                aria-label={t('patientReports.toc_numbering')}
              >
                {TOC_NUMBERING_ORDER.map((numbering) => (
                  <Radio
                    key={numbering}
                    id={`toc-numbering-${section.id}-${numbering}`}
                    name={`toc-numbering-${section.id}`}
                    label={t(TOC_NUMBERING_LABEL[numbering])}
                    checked={section.numbering === numbering}
                    disabled={disabled}
                    onChange={() => onChange({ ...section, numbering })}
                  />
                ))}
              </div>
            </fieldset>
          ) : null}
          <fieldset className="mt-s3" disabled={disabled}>
            <legend className={cn(TYPE.small, 'font-semibold text-ds-text')}>
              {t('patientReports.heading_style')}
            </legend>
            <p className={cn(TYPE.small, 'text-ds-muted')}>
              {t(
                section.kind === 'toc'
                  ? 'patientReports.heading_style_toc_hint'
                  : 'patientReports.heading_style_hint',
              )}
            </p>
            <div
              className="mt-s2 flex flex-col gap-s1"
              role="radiogroup"
              aria-label={t('patientReports.heading_style')}
            >
              {HEADING_STYLE_ORDER.map((style) => (
                <Radio
                  key={style}
                  id={`heading-style-${section.id}-${style}`}
                  name={`heading-style-${section.id}`}
                  label={t(HEADING_STYLE_LABEL[style])}
                  checked={headingStyleOf(section.headingStyle) === style}
                  disabled={disabled}
                  onChange={() =>
                    onChange({
                      ...section,
                      headingStyle: style === 'bold' ? undefined : style,
                    })
                  }
                />
              ))}
            </div>
          </fieldset>
          <Checkbox
            className="mt-s3"
            label={t('patientReports.page_break_after')}
            hint={t('patientReports.page_break_after_hint')}
            checked={section.pageBreakAfter === true}
            disabled={disabled}
            onChange={(event) => onChange({ ...section, pageBreakAfter: event.target.checked })}
          />
        </div>
      ) : null}
    </article>
  );
}

function journalSelectionSummary(
  section: Extract<ReportSection, { kind: 'journal' }>,
  t: (key: string) => string,
): string {
  if (section.noteIds == null && section.limit) {
    return t('patientReports.journal_limit_legacy').replace('{{count}}', String(section.limit));
  }
  return countSummary(
    section.noteIds?.length ?? 0,
    t('patientReports.journal_selected_none'),
    t('patientReports.journal_selected_one'),
    t('patientReports.journal_selected_other'),
  );
}

function markingSelectionSummary(
  section: Extract<ReportSection, { kind: 'markings' }>,
  t: (key: string) => string,
): string {
  return countSummary(
    section.markingIds.length,
    t('patientReports.marking_selected_none'),
    t('patientReports.marking_selected_one'),
    t('patientReports.marking_selected_other'),
  );
}

function fileSelectionSummary(
  section: Extract<ReportSection, { kind: 'file' }>,
  t: (key: string) => string,
): string {
  return countSummary(
    section.fileIds.length,
    t('patientReports.file_selected_none'),
    t('patientReports.file_selected_one'),
    t('patientReports.file_selected_other'),
  );
}

function FilePicker({
  section,
  files,
  disabled,
  onChange,
}: {
  section: Extract<ReportSection, { kind: 'file' }>;
  files: readonly FileSource[] | undefined;
  disabled?: boolean;
  onChange: (section: ReportSection) => void;
}) {
  const { t } = useTranslation();
  if (files == null) {
    return (
      <p className={cn(TYPE.small, 'mt-s3 text-ds-muted')}>{t('patientReports.file_on_report')}</p>
    );
  }
  return (
    <div className="mt-s3">
      <EntryChecks
        hint={t('patientReports.file_pick')}
        empty={t('patientReports.none_in_group')}
        disabled={disabled}
        rows={files.map((file) => ({
          id: file.id,
          label: file.filename,
          testId: `report-file-${file.id}`,
        }))}
        selected={section.fileIds}
        onToggle={(id, checked) => {
          const fileIds = checked
            ? [...section.fileIds, id].filter((item, index, all) => all.indexOf(item) === index)
            : section.fileIds.filter((item) => item !== id);
          onChange({ ...section, fileIds });
        }}
      />
    </div>
  );
}

function countSummary(count: number, none: string, one: string, many: string): string {
  if (count <= 0) return none;
  if (count === 1) return one;
  return many.replace('{{count}}', String(count));
}

function JournalNotePicker({
  section,
  notes,
  disabled,
  onChange,
}: {
  section: Extract<ReportSection, { kind: 'journal' }>;
  notes: readonly JournalNoteSource[] | undefined;
  disabled?: boolean;
  onChange: (section: ReportSection) => void;
}) {
  const { t } = useTranslation();
  const chosen =
    section.noteIds ??
    selectedJournalNotes(section, {
      ...EMPTY_REPORT_CATALOG,
      journalNotes: [...(notes ?? [])],
    }).map((note) => note.id);
  const ordered = orderedJournalNotes(notes ?? [], section.order);
  return (
    <div className="mt-s3 flex flex-col gap-s3">
      <Select
        label={t('patientReports.order_label')}
        disabled={disabled}
        value={section.order}
        onChange={(event) =>
          onChange({
            ...section,
            order: event.target.value === 'oldest' ? 'oldest' : 'newest',
          })
        }
      >
        <option value="newest">{t('patientReports.order_newest')}</option>
        <option value="oldest">{t('patientReports.order_oldest')}</option>
      </Select>
      {notes == null ? (
        <p className={cn(TYPE.small, 'text-ds-muted')}>{t('patientReports.journal_on_report')}</p>
      ) : (
        <EntryChecks
          hint={t('patientReports.journal_pick')}
          empty={t('patientReports.none_in_group')}
          disabled={disabled}
          rows={ordered.map((note) => ({
            id: note.id,
            label: noteLabel(note),
            testId: `report-journal-note-${note.id}`,
          }))}
          selected={chosen}
          onToggle={(id, checked) => {
            const next = checked ? [...chosen, id] : chosen.filter((item) => item !== id);
            onChange({
              ...section,
              noteIds: next.filter((item, index, all) => all.indexOf(item) === index),
              limit: null,
            });
          }}
        />
      )}
    </div>
  );
}

function MarkingPicker({
  section,
  markings,
  tags,
  disabled,
  onChange,
}: {
  section: Extract<ReportSection, { kind: 'markings' }>;
  markings: readonly MarkingSource[] | undefined;
  tags: readonly { id: string; name: string }[];
  disabled?: boolean;
  onChange: (section: ReportSection) => void;
}) {
  const { t } = useTranslation();
  if (markings == null) {
    return (
      <p className={cn(TYPE.small, 'text-ds-muted')}>{t('patientReports.marking_on_report')}</p>
    );
  }
  const ordered = orderedMarkings(markings, section.order);
  const visible = ordered.filter((marking) => {
    if (section.markingIds.includes(marking.id)) return true;
    if (section.tagIds.length === 0) return true;
    return section.tagIds.some((tagId) => marking.tagIds.includes(tagId));
  });
  return (
    <>
      {tags.length > 0 ? (
        <div>
          <p className={cn(TYPE.small, 'font-semibold text-ds-text')}>
            {t('patientReports.tags_label')}
          </p>
          <p className={cn(TYPE.small, 'text-ds-muted')}>{t('patientReports.tags_filter')}</p>
          <div className="mt-s2 flex flex-col gap-s1">
            {tags.map((tag) => (
              <Checkbox
                key={tag.id}
                label={tag.name}
                checked={section.tagIds.includes(tag.id)}
                disabled={disabled}
                onChange={(event) => {
                  const tagIds = event.target.checked
                    ? [...section.tagIds, tag.id]
                    : section.tagIds.filter((id) => id !== tag.id);
                  onChange({ ...section, tagIds });
                }}
              />
            ))}
          </div>
        </div>
      ) : null}
      <EntryChecks
        hint={t('patientReports.marking_pick')}
        empty={t('patientReports.none_in_group')}
        disabled={disabled}
        rows={visible.map((marking) => ({
          id: marking.id,
          label: markingLabel(marking),
          testId: `report-marking-${marking.id}`,
        }))}
        selected={section.markingIds}
        onToggle={(id, checked) => {
          const markingIds = checked
            ? [...section.markingIds, id].filter((item, index, all) => all.indexOf(item) === index)
            : section.markingIds.filter((item) => item !== id);
          onChange({ ...section, markingIds });
        }}
      />
    </>
  );
}

function EntryChecks({
  hint,
  empty,
  rows,
  selected,
  disabled,
  onToggle,
}: {
  hint: string;
  empty: string;
  rows: readonly { id: string; label: string; testId: string }[];
  selected: readonly string[];
  disabled?: boolean;
  onToggle: (id: string, checked: boolean) => void;
}) {
  return (
    <div>
      <p className={cn(TYPE.small, 'text-ds-muted')}>{hint}</p>
      <div className="mt-s2 flex max-h-64 flex-col gap-s1 overflow-y-auto">
        {rows.length === 0 ? (
          <p className={cn(TYPE.small, 'text-ds-muted')}>{empty}</p>
        ) : (
          rows.map((row) => (
            <Checkbox
              key={row.id}
              data-testid={row.testId}
              label={<span className="line-clamp-2">{row.label}</span>}
              checked={selected.includes(row.id)}
              disabled={disabled}
              onChange={(event) => onToggle(row.id, event.target.checked)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function noteLabel(note: JournalNoteSource): string {
  const title = note.title?.trim() ?? '';
  return [note.entryDate, title].filter(Boolean).join(' — ') || note.id;
}

function markingLabel(marking: MarkingSource): string {
  const name = marking.filename.trim() || marking.quote.trim() || marking.id;
  return name.length > 160 ? `${name.slice(0, 157)}…` : name;
}

function orderedJournalNotes(
  notes: readonly JournalNoteSource[],
  order: 'newest' | 'oldest',
): JournalNoteSource[] {
  return [...notes].sort((a, b) => {
    const cmp = a.entryDate.localeCompare(b.entryDate);
    return order === 'oldest' ? cmp : -cmp;
  });
}

function orderedMarkings(
  markings: readonly MarkingSource[],
  order: 'newest' | 'oldest' | 'file',
): MarkingSource[] {
  return [...markings].sort((a, b) => {
    if (order === 'file') {
      return (
        a.filename.localeCompare(b.filename, 'da') ||
        (a.createdAt ?? '').localeCompare(b.createdAt ?? '')
      );
    }
    const cmp = (a.createdAt ?? '').localeCompare(b.createdAt ?? '');
    return order === 'oldest' ? cmp : -cmp;
  });
}

function UserTextFields({
  section,
  textTypes,
  userTexts,
  disabled,
  onChange,
}: {
  section: Extract<ReportSection, { kind: 'user_text' }>;
  textTypes: readonly { id: string; name: string }[];
  userTexts?: readonly { id: string; typeId: string; body: string }[];
  disabled?: boolean;
  onChange: (section: ReportSection) => void;
}) {
  const { t } = useTranslation();
  const matches = (userTexts ?? []).filter((text) => text.typeId === section.typeId);
  const pick = (typeId: string) => {
    const typeName = textTypes.find((type) => type.id === typeId)?.name ?? '';
    const follows =
      !section.heading.trim() ||
      section.heading === t('patientReports.add_user_text') ||
      textTypes.some((type) => type.name === section.heading);
    const ofType = (userTexts ?? []).filter((text) => text.typeId === typeId);
    const textId = userTexts && ofType.length === 1 ? (ofType[0]?.id ?? null) : null;
    onChange({
      ...section,
      typeId: typeId || null,
      textId,
      heading: follows && typeName ? typeName : section.heading,
    });
  };
  return (
    <div className="mt-s3 flex flex-col gap-s2">
      <Select
        id={`report-user-text-type-${section.id}`}
        label={t('patientReports.user_text_type')}
        value={section.typeId ?? ''}
        disabled={disabled || textTypes.length === 0}
        hint={
          textTypes.length === 0
            ? t('patientReports.texts_empty')
            : t('patientReports.user_text_hint')
        }
        onChange={(event) => pick(event.target.value)}
      >
        <option value="">{t('patientReports.user_text_type_none')}</option>
        {textTypes.map((type) => (
          <option key={type.id} value={type.id}>
            {type.name}
          </option>
        ))}
      </Select>
      {userTexts && matches.length > 1 ? (
        <Select
          id={`report-user-text-choice-${section.id}`}
          label={t('patientReports.user_text_pick_label')}
          value={section.textId ?? ''}
          disabled={disabled}
          hint={t('patientReports.user_text_pick')}
          onChange={(event) => onChange({ ...section, textId: event.target.value || null })}
        >
          <option value="">{t('patientReports.user_text_pick_none')}</option>
          {matches.map((text) => (
            <option key={text.id} value={text.id}>
              {personalTextChoiceLabel(text.body, t('patientReports.texts_pick_empty'))}
            </option>
          ))}
        </Select>
      ) : null}
    </div>
  );
}

function questionnaireCardIsMissing(
  section: ReportSection,
  missingFormIds: readonly string[] | undefined,
): boolean {
  if (section.kind !== 'questionnaire' || !missingFormIds?.length) return false;
  const keys = [section.formsTemplateId, section.formsTemplateName.trim()].filter(
    (key): key is string => Boolean(key),
  );
  return keys.some((key) => missingFormIds.includes(key));
}
